import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { View, Text, Pressable, Alert, PanResponder } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  runOnJS,
} from 'react-native-reanimated';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface CourseModule {
  _id: string;
  title: string;
  description: string;
  content: string;
  order: number;
  duration: number; // in minutes
  type: 'video' | 'text' | 'quiz' | 'assignment';
  completed?: boolean;
  resources?: Array<{
    _id: string;
    title: string;
    url: string;
    type: 'file' | 'link' | 'video';
  }>;
}

interface HierarchyPosition {
  x: number;
  y: number;
}

export interface DraggableModuleCardHandle {
  getPosition: () => HierarchyPosition;
  setPosition: (position: HierarchyPosition) => void;
  animateToPosition: (position: HierarchyPosition) => void;
}

interface DraggableModuleCardProps {
  module: CourseModule;
  initialPosition: HierarchyPosition;
  onPositionChange: (position: HierarchyPosition) => void;
  onPositionUpdate?: (position: HierarchyPosition) => void; // For continuous updates during drag
  dragEnabled?: boolean;
  isPrimary?: boolean;
  isEditMode?: boolean;
  zoomLevel?: number;
  onModuleClick?: (module: CourseModule) => void;
  onEditModule?: (moduleId: string) => void;
  onDeleteModule?: (moduleId: string) => void;
  onAddSubModule?: (parentModuleId: string) => void;
  // Arrow and snap handling (similar to DraggableUserCard)
  onSnap?: (target: string, source: string) => void;
  arrowHandler?: ({ id, path, reset }: { id: string, path: string, reset?: boolean }) => void;
  zoomProps?: { zoomLevel: number; offsetX: number; offsetY: number };
  getAllModulePositions?: () => Record<string, HierarchyPosition>;
  // Professional link positioning
  linkStartPosition?: 'left' | 'right' | 'top' | 'bottom';
  linkEndPosition?: 'left' | 'right' | 'top' | 'bottom';
  onComponentMeasured?: (measurements: { width: number; height: number; x: number; y: number }) => void;
}

const DraggableModuleCard = forwardRef<DraggableModuleCardHandle, DraggableModuleCardProps>((props, ref) => {
  const {
    module,
    initialPosition,
    onPositionChange,
    onPositionUpdate,
    dragEnabled = true,
    isPrimary = false,
    isEditMode = true,
    zoomLevel = 1,
    onModuleClick,
    onEditModule,
    onDeleteModule,
    onAddSubModule,
    onSnap,
    arrowHandler,
    zoomProps,
    getAllModulePositions,
    linkStartPosition = 'right', // Default to right side
    linkEndPosition = 'left', // Default to left side for incoming links
    onComponentMeasured
  } = props;

  const theme = themeSelector();

  // Animation values
  const translateX = useSharedValue(initialPosition.x);
  const translateY = useSharedValue(initialPosition.y);
  const scale = useSharedValue(1);
  const opacity = useSharedValue(1);

  const componentDimensions = { width: 280, height: 160 };

  // State
  const [isDragging, setIsDragging] = useState(false);

  // Measurement state
  const [componentMeasurements, setComponentMeasurements] = useState({
    width: componentDimensions.width,
    height: componentDimensions.height,
    x: 0,
    y: 0
  });

  // Calculate connection point based on linkStartPosition
  const getConnectionPoint = useCallback((position: HierarchyPosition, side: 'left' | 'right' | 'top' | 'bottom') => {
    const { width, height } = componentMeasurements;

    switch (side) {
      case 'left':
        return { x: position.x, y: position.y + height / 2 };
      case 'right':
        return { x: position.x + width, y: position.y + height / 2 };
      case 'top':
        return { x: position.x + width / 2, y: position.y };
      case 'bottom':
        return { x: position.x + width / 2, y: position.y + height };
      default:
        return { x: position.x + width / 2, y: position.y + height / 2 };
    }
  }, [componentMeasurements]);

  // Get module type icon and color
  const getModuleTypeInfo = useCallback((type: string) => {
    switch (type) {
      case 'video':
        return {
          icon: 'play-circle',
          color: theme ? '#ef4444' : '#f87171',
          bgColor: theme ? '#fef2f2' : '#7f1d1d'
        };
      case 'text':
        return {
          icon: 'text-box',
          color: theme ? '#3b82f6' : '#60a5fa',
          bgColor: theme ? '#eff6ff' : '#1e3a8a'
        };
      case 'quiz':
        return {
          icon: 'help-circle',
          color: theme ? '#f59e0b' : '#fbbf24',
          bgColor: theme ? '#fffbeb' : '#92400e'
        };
      case 'assignment':
        return {
          icon: 'clipboard-check',
          color: theme ? '#10b981' : '#34d399',
          bgColor: theme ? '#f0fdf4' : '#064e3b'
        };
      default:
        return {
          icon: 'book',
          color: theme ? '#6b7280' : '#9ca3af',
          bgColor: theme ? '#f9fafb' : '#1f2937'
        };
    }
  }, [theme]);

  const moduleTypeInfo = getModuleTypeInfo(module.type);

  // Convert gesture values to view coordinates (from DraggableUserCard)
  const convertGestureToViewCoordinates = useCallback(({
    absoluteX,
    absoluteY,
    translationX,
    translationY,
    parentOffset,
    zoomLevel,
  }) => {
    const adjustedX = (absoluteX - parentOffset.x) / zoomLevel;
    const adjustedY = (absoluteY - parentOffset.y) / zoomLevel;

    const translatedViewX = adjustedX + translationX / zoomLevel;
    const translatedViewY = adjustedY + translationY / zoomLevel;

    return { viewX: translatedViewX, viewY: translatedViewY };
  }, []);

  // Arrow PanResponder (for connection drawing) - simplified without internal SVG
  const arrowPanResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => dragEnabled && !isDragging && isEditMode,
    onPanResponderGrant: (event, gestureState) => {
      const { locationX: viewX, locationY: viewY } = event.nativeEvent;
      const currentZoomLevel = zoomProps?.zoomLevel || zoomLevel || 1;

      // Log arrow start with parent dimensions
      const parentDimensions = getAllModulePositions ? Object.keys(getAllModulePositions()).length : 0;
      console.log('🎯 Arrow movement started:', {
        moduleId: module._id,
        moduleTitle: module.title,
        parentViewModules: parentDimensions,
        cardDimensions: componentDimensions,
        initialPosition: { x: translateX.value, y: translateY.value }
      });

      // Set arrow mode but don't interfere with drag state
      scale.value = withSpring(1.05);
      opacity.value = withSpring(0.9);
    },
    onPanResponderMove: (event, gestureState) => {
      const {
        dx: tX,
        dy: tY,
        moveX: ax,
        moveY: ay,
      } = gestureState;
      const currentZoomProps = zoomProps || { offsetX: 0, offsetY: 0, zoomLevel: zoomLevel || 1 };
      const { offsetX, offsetY, zoomLevel: currentZoomLevel } = currentZoomProps;

      // Get all module positions for arrow detection
      const allModulePositions = getAllModulePositions ? getAllModulePositions() : {};
      const currentModulePosition = allModulePositions[module._id] || { x: translateX.value, y: translateY.value };

      // Calculate current drag position in world coordinates
      const interpolatedX = tX / currentZoomLevel;
      const interpolatedY = tY / currentZoomLevel;
      const dragX = interpolatedX;
      const dragY = interpolatedY;

      // Calculate current card center in world coordinates
      // Calculate connection point for arrow starting point
      const connectionPoint = getConnectionPoint(currentModulePosition, linkStartPosition);

      // Convert drag position to world coordinates (from connection point)
      const worldDragX = connectionPoint.x + dragX;
      const worldDragY = connectionPoint.y + dragY;

      // Iterate over all module positions to check snapping
      let snappedToModule = false;
      Object.entries(allModulePositions).forEach(([moduleId, position], index) => {
        if (moduleId === module._id) return; // Skip self
        if (snappedToModule) return; // Already snapped to someone

        const targetX = position.x;
        const targetY = position.y;

        // Calculate target connection point (default to left side for incoming links)
        const targetConnectionPoint = getConnectionPoint({ x: targetX, y: targetY }, linkEndPosition);

        // Calculate distance from drag point to target connection point
        const distance = Math.sqrt(
          Math.pow(targetConnectionPoint.x - worldDragX, 2) + Math.pow(targetConnectionPoint.y - worldDragY, 2)
        );

        if (distance < 150) { // Snap threshold
          console.log(`✅ SNAPPING to ${moduleId}! Distance: ${distance.toFixed(1)}`);

          // Calculate relative positions for arrow path from connection point to target connection point
          const relativeTargetX = targetConnectionPoint.x - connectionPoint.x;
          const relativeTargetY = targetConnectionPoint.y - connectionPoint.y;

          // Create smooth bezier curve
          const controlPoint1X = relativeTargetX * 0.3;
          const controlPoint1Y = relativeTargetY * 0.3;
          const controlPoint2X = relativeTargetX * 0.7;
          const controlPoint2Y = relativeTargetY * 0.7;

          const arrowPathValue = `M0,0 C${controlPoint1X},${controlPoint1Y} ${controlPoint2X},${controlPoint2Y} ${relativeTargetX},${relativeTargetY}`;

          if (arrowHandler) {
            // Send absolute coordinates for snapped state
            const absoluteSnappedPath = `M${connectionPoint.x},${connectionPoint.y} C${connectionPoint.x + controlPoint1X},${connectionPoint.y + controlPoint1Y} ${connectionPoint.x + controlPoint2X},${connectionPoint.y + controlPoint2Y} ${targetConnectionPoint.x},${targetConnectionPoint.y}`;
            runOnJS(arrowHandler)({ id: moduleId, path: absoluteSnappedPath });
          }

          snappedToModule = true;
        }
      });

      // Handle unsnapped state - draw arrow following the drag
      if (!snappedToModule) {
        // Create a simple line from connection point to drag position
        const endX = connectionPoint.x + dragX;
        const endY = connectionPoint.y + dragY;

        // Create control points for a smooth curve
        const controlPoint1X = connectionPoint.x + (dragX * 0.3);
        const controlPoint1Y = connectionPoint.y + (dragY * 0.3);
        const controlPoint2X = connectionPoint.x + (dragX * 0.7);
        const controlPoint2Y = connectionPoint.y + (dragY * 0.7);

        const absolutePath = `M${connectionPoint.x},${connectionPoint.y} C${controlPoint1X},${controlPoint1Y} ${controlPoint2X},${controlPoint2Y} ${endX},${endY}`;

        if (arrowHandler) {
          // Throttle arrow updates to reduce buggy behavior
          runOnJS(arrowHandler)({
            id: module._id,
            path: absolutePath,
          });
        }
      }
    },
    onPanResponderRelease: () => {
      if (arrowHandler) {
        runOnJS(arrowHandler)({ id: module._id, path: `M0,0 L0,0`, reset: true });
      }

      // Reset visual state
      scale.value = withSpring(1);
      opacity.value = withSpring(1);
    },
  });

  // Drag PanResponder (for moving the card) - separate from arrow
  const dragPanResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => dragEnabled && !isDragging,
    onPanResponderGrant: (event, gestureState) => {
      console.log('🎯 Drag start for:', module._id);
      setIsDragging(true);
      scale.value = withSpring(1.05);
      opacity.value = withSpring(0.9);
    },
    onPanResponderMove: (event, gestureState) => {
      if (!isDragging) return;

      const { dx, dy } = gestureState;
      translateX.value = initialPosition.x + dx;
      translateY.value = initialPosition.y + dy;

      // Update position continuously during drag for real-time link updates (only refs, no re-renders)
      const newPosition = {
        x: initialPosition.x + dx,
        y: initialPosition.y + dy
      };

      // Use onPositionUpdate for continuous updates (only updates refs)
      if (onPositionUpdate) {
        onPositionUpdate(newPosition);
      }
    },
    onPanResponderRelease: (event, gestureState) => {
      if (!isDragging) return;

      // Snap to grid for normal drag
      const gridSize = 50;
      const snappedX = Math.round(translateX.value / gridSize) * gridSize;
      const snappedY = Math.round(translateY.value / gridSize) * gridSize;

      translateX.value = withSpring(snappedX);
      translateY.value = withSpring(snappedY);

      const finalPosition = { x: snappedX, y: snappedY };
      onPositionChange(finalPosition);

      setIsDragging(false);
      scale.value = withSpring(1);
      opacity.value = withSpring(1);
    },
  });

  // Imperative handle for external control
  useImperativeHandle(ref, () => ({
    getPosition: () => ({
      x: translateX.value,
      y: translateY.value,
    }),
    setPosition: (position: HierarchyPosition) => {
      translateX.value = position.x;
      translateY.value = position.y;
      onPositionChange(position);
    },
    animateToPosition: (position: HierarchyPosition) => {
      translateX.value = withSpring(position.x);
      translateY.value = withSpring(position.y);
      onPositionChange(position);
    },
  }), [onPositionChange]);


  // Animated styles
  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [
        { translateX: translateX.value },
        { translateY: translateY.value },
        { scale: scale.value },
        // Removed zoom scaling - let BaseHierarchy handle zoom transformations
      ],
      opacity: opacity.value,
    };
  });

  const handleModuleClick = useCallback(() => {
    if (onModuleClick) {
      onModuleClick(module);
    }
  }, [module, onModuleClick]);

  const handleEditPress = useCallback(() => {
    if (onEditModule) {
      onEditModule(module._id);
    }
  }, [module._id, onEditModule]);

  const handleDeletePress = useCallback(() => {
    Alert.alert(
      'Delete Module',
      `Are you sure you want to delete "${module.title}"?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: () => {
            if (onDeleteModule) {
              onDeleteModule(module._id);
            }
          },
        },
      ]
    );
  }, [module._id, module.title, onDeleteModule]);

  return (
    <Animated.View
      style={[
        {
          position: 'absolute',
          width: componentDimensions.width, // 280
          height: componentDimensions.height, // 160
          zIndex: isDragging ? 10 : 1,
        },
        animatedStyle,
      ]}
      onLayout={(event) => {
        const { x, y, width, height } = event.nativeEvent.layout;
        const measurements = { x, y, width, height };
        setComponentMeasurements(measurements);
        onComponentMeasured?.(measurements);
      }}
    >
      {/* Main Card Content with Arrow PanResponder */}
      <View
        {...arrowPanResponder.panHandlers}
        style={{
          flex: 1,
          backgroundColor: theme ? '#ffffff' : '#1e293b',
          borderRadius: 16,
          borderWidth: 2,
          borderColor: isPrimary
            ? (theme ? '#3b82f6' : '#60a5fa')
            : (theme ? '#e5e7eb' : '#374151'),
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.1,
          shadowRadius: 8,
          elevation: 4,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          padding: 12,
          backgroundColor: isPrimary
            ? (theme ? '#3b82f6' : '#1e40af')
            : moduleTypeInfo.bgColor,
          gap: 8,
        }}>
          <MaterialCommunityIcons
            name={moduleTypeInfo.icon as any}
            size={20}
            color={isPrimary
              ? '#ffffff'
              : moduleTypeInfo.color}
          />
          <Text
            style={{
              flex: 1,
              fontSize: 14,
              fontWeight: '600',
              color: isPrimary
                ? '#ffffff'
                : (theme ? '#1f2937' : '#f9fafb'),
            }}
            numberOfLines={1}
          >
            {module.title}
          </Text>

          {/* Action buttons for edit mode */}
          {isEditMode && !isPrimary && (
            <View style={{ flexDirection: 'row', gap: 4 }}>
              <Pressable
                onPress={handleEditPress}
                style={{
                  padding: 4,
                  borderRadius: 4,
                  backgroundColor: theme ? '#f3f4f6' : '#374151',
                }}
              >
                <MaterialCommunityIcons
                  name="pencil"
                  size={14}
                  color={theme ? '#6b7280' : '#9ca3af'}
                />
              </Pressable>
              <Pressable
                onPress={handleDeletePress}
                style={{
                  padding: 4,
                  borderRadius: 4,
                  backgroundColor: theme ? '#fef2f2' : '#7f1d1d',
                }}
              >
                <MaterialCommunityIcons
                  name="delete"
                  size={14}
                  color="#ef4444"
                />
              </Pressable>
              <Pressable
                onPress={() => {
                  console.log('➕ Sub-module button pressed:', { moduleId: module._id, onAddSubModule: !!onAddSubModule });
                  if (onAddSubModule) {
                    onAddSubModule(module._id);
                  } else {
                    console.error('❌ onAddSubModule is not defined');
                  }
                }}
                style={{
                  padding: 4,
                  borderRadius: 4,
                  backgroundColor: theme ? '#f0f9ff' : '#1e3a8a',
                }}
              >
                <MaterialCommunityIcons
                  name="plus"
                  size={14}
                  color="#3b82f6"
                />
              </Pressable>
            </View>
          )}
        </View>

        {/* Content */}
        <Pressable
          onPress={handleModuleClick}
          style={{
            flex: 1,
            padding: 12,
            justifyContent: 'space-between',
          }}
        >
          <Text
            style={{
              fontSize: 12,
              color: theme ? '#6b7280' : '#9ca3af',
              lineHeight: 16,
            }}
            numberOfLines={2}
          >
            {module.description}
          </Text>

          {/* Footer */}
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 8,
          }}>
            <Text
              style={{
                fontSize: 11,
                color: theme ? '#9ca3af' : '#6b7280',
                fontWeight: '500',
              }}
            >
              {module.duration} min
            </Text>

            {module.completed && (
              <MaterialCommunityIcons
                name="check-circle"
                size={16}
                color="#10b981"
              />
            )}
          </View>
        </Pressable>
      </View>

      {/* Drag Handle - separate from main card */}
      <View
        {...dragPanResponder.panHandlers}
        style={{
          position: 'absolute',
          right: -15,
          top: '50%',
          marginTop: -15,
          width: 30,
          height: 30,
          borderRadius: 15,
          backgroundColor: theme ? '#3b82f6' : '#1e40af',
          borderWidth: 2,
          borderColor: theme ? '#ffffff' : '#1e293b',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 15,
        }}
      >
        <MaterialCommunityIcons
          name="drag"
          size={16}
          color="#ffffff"
        />
      </View>

    </Animated.View>
  );
});

DraggableModuleCard.displayName = 'DraggableModuleCard';

export default DraggableModuleCard;